import { useQuery } from '@apollo/client'
import InfiniteScroll from 'react-infinite-scroll-component'
import React, { useMemo, useState } from 'react'

import Flex from 'shared/ui/Flex'
import { Skeleton } from 'shared/ui/Skeleton'
import SearchInput from 'shared/ui/SearchInput'
import useAuth from 'data/gradoo/hooks/useAuth'
import useContentMarks, {
  ContentMarkType
} from 'shared/hooks/useContentMarks'
import { CUSTOM_PAGES_INSTANCES_RESULT } from 'data/gradoo/queries/custom'

import { PanelProps, Panels } from '../../types'
import ReportItem from '../ReportsPanel/components/ReportItem'

const PAGE_SIZE = 100

type CustomPagePanelProps = PanelProps & {
  moduleInstanceId?: string
}

const CustomPagesPanel: React.FC<CustomPagePanelProps> = ({
  moduleInstanceId,
  push
}) => {
  const [search, setSearch] = useState('')
  const { authGroupId } = useAuth()

  const { markedIds, toggleMark } = useContentMarks({
    groupId: authGroupId ?? undefined,
    moduleInstanceId
  })

  const {
    data: moduleInstanceResultsData,
    loading,
    fetchMore
  } = useQuery(CUSTOM_PAGES_INSTANCES_RESULT, {
    skip: !authGroupId || !moduleInstanceId,
    variables: {
      first: PAGE_SIZE,
      moduleInstance: moduleInstanceId as string
    },
    notifyOnNetworkStatusChange: true,
    context: { client: 'gradoo' }
  })

  const edges = useMemo(
    () =>
      moduleInstanceResultsData?.customPagesInstancesResult?.edges ?? [],
    [moduleInstanceResultsData]
  )
  const hasNextPage =
    moduleInstanceResultsData?.customPagesInstancesResult?.pageInfo
      .hasNextPage ?? false
  const endCursor =
    moduleInstanceResultsData?.customPagesInstancesResult?.pageInfo.endCursor

  const onLoadMore = async () => {
    if (!hasNextPage || !moduleInstanceId) {
      return
    }
    await fetchMore({
      variables: {
        moduleInstance: moduleInstanceId,
        after: endCursor
      }
    })
  }

  const filteredEdges = useMemo(() => {
    const term = search.toLocaleLowerCase()
    return edges.filter(edge =>
      edge?.node?.title?.toLocaleLowerCase().includes(term)
    )
  }, [edges, search])

  const loadingUI = new Array(3)
    .fill(null)
    .map((_, i) => <Skeleton key={i} height={48} />)

  return (
    <Flex direction="column">
      <SearchInput
        value={search}
        onChange={setSearch}
        marginBottom={26}
      />
      <Flex direction="column" rowGap={16}>
        {loading && edges.length === 0 ? (
          loadingUI
        ) : (
          <InfiniteScroll
            dataLength={filteredEdges.length}
            next={onLoadMore}
            hasMore={hasNextPage}
            loader={<Skeleton height={48} />}
            scrollThreshold={0.8}
            height={window.innerHeight}
          >
            <Flex direction="column" rowGap={16}>
              {filteredEdges.map(
                customPageInstance =>
                  customPageInstance?.node && (
                    <ReportItem
                      key={customPageInstance.node.id}
                      data-testid={`CustomPageItem:${customPageInstance.node.id}`}
                      onClick={() =>
                        push({
                          panel: Panels.customPage,
                          title:
                            customPageInstance?.node?.title,
                          props: {
                            customPageInstanceId:
                              customPageInstance?.node?.id
                          }
                        })
                      }
                      title={customPageInstance.node.title}
                      isMarked={markedIds.has(
                        customPageInstance.node.id
                      )}
                      onToggleMark={() =>
                        toggleMark(
                          customPageInstance.node!.id,
                          ContentMarkType.CustomPage
                        )
                      }
                    />
                  )
              )}
            </Flex>
          </InfiniteScroll>
        )}
      </Flex>
    </Flex>
  )
}

export default CustomPagesPanel
